import React, { useState } from "react";
import PageHero from "../components/layout/PageHero";
import Reveal from "../components/Reveal";
import { useContent } from "../context/ContentContext";
import { api } from "../api";
import { Mail, Phone, MapPin, Send, CheckCircle2, Globe2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { useRFQ } from "../context/RFQContext";

export default function Contact() {
  const { toast } = useToast();
  const { openRFQ } = useRFQ();
  const { content } = useContent();
  const company = content.company || {};
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast({ title: "Message sent", description: "Thank you — we'll be in touch soon." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again shortly.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Let's talk about your sourcing needs"
        subtitle="Reach out to discuss products, markets and requirements. Our team responds quickly with clarity and transparency."
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10">
          {/* Info */}
          <div className="lg:col-span-2">
            <Reveal>
              <h2 className="font-display text-[28px] text-foreground">Get in touch</h2>
              <p className="mt-3 text-[15px] text-muted-foreground leading-relaxed">
                We'd love to help you source quality pharmaceutical products for your market.
              </p>
            </Reveal>
            <div className="mt-8 space-y-4">
              <Reveal>
                <a href={`mailto:${company.email}`} className="flex items-start gap-4 bg-card rounded-2xl border border-border p-5 hover:border-[#2ED9B8]/30 transition-colors">
                  <span className="h-11 w-11 rounded-xl bg-[#0f2236] flex items-center justify-center shrink-0"><Mail className="h-5 w-5 text-[#2ED9B8]" /></span>
                  <span>
                    <span className="block text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</span>
                    <span className="block font-medium text-foreground mt-0.5">{company.email}</span>
                  </span>
                </a>
              </Reveal>
              <Reveal delay={80}>
                <a href={`tel:${company.phoneHref}`} className="flex items-start gap-4 bg-card rounded-2xl border border-border p-5 hover:border-[#2ED9B8]/30 transition-colors">
                  <span className="h-11 w-11 rounded-xl bg-[#0f2236] flex items-center justify-center shrink-0"><Phone className="h-5 w-5 text-[#2ED9B8]" /></span>
                  <span>
                    <span className="block text-xs uppercase tracking-[0.15em] text-muted-foreground">Call us</span>
                    <span className="block font-medium text-foreground mt-0.5">{company.phone}</span>
                  </span>
                </a>
              </Reveal>
              <Reveal delay={160}>
                <div className="flex items-start gap-4 bg-card rounded-2xl border border-border p-5">
                  <span className="h-11 w-11 rounded-xl bg-[#0f2236] flex items-center justify-center shrink-0"><Globe2 className="h-5 w-5 text-[#2ED9B8]" /></span>
                  <span>
                    <span className="block text-xs uppercase tracking-[0.15em] text-muted-foreground">Reach</span>
                    <span className="block font-medium text-foreground mt-0.5">Serving 50+ countries worldwide</span>
                  </span>
                </div>
              </Reveal>
            </div>

            <Reveal delay={200}>
              <div className="mt-8 rounded-2xl brand-gradient p-6 text-white">
                <p className="font-display text-[20px]">Have a specific requirement?</p>
                <p className="text-sm text-white/80 mt-2">Submit a detailed RFQ and get matched with qualified partners.</p>
                <button onClick={openRFQ} className="mt-4 inline-flex items-center bg-card text-[#2ED9B8] rounded-full px-5 h-11 font-medium hover:bg-[#0f2236] transition-colors">
                  Request an RFQ
                </button>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <Reveal>
              <div className="bg-card rounded-[22px] border border-border p-7 lg:p-9 shadow-[0_30px_70px_-40px_rgba(12,97,82,0.4)]">
                {sent ? (
                  <div className="py-14 text-center">
                    <div className="h-14 w-14 rounded-full bg-[#0f2236] flex items-center justify-center mx-auto">
                      <CheckCircle2 className="h-7 w-7 text-[#2ED9B8]" />
                    </div>
                    <h3 className="mt-5 font-display text-2xl text-foreground">Message sent</h3>
                    <p className="mt-2 text-sm text-muted-foreground">Thank you for reaching out. Our team will respond shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="c-name">Full name</Label>
                        <Input id="c-name" name="name" required value={form.name} onChange={handleChange} placeholder="Your name" className="h-11" />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="c-company">Company</Label>
                        <Input id="c-company" name="company" value={form.company} onChange={handleChange} placeholder="Company" className="h-11" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="c-email">Email</Label>
                      <Input id="c-email" name="email" type="email" required value={form.email} onChange={handleChange} placeholder="you@company.com" className="h-11" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="c-message">Message</Label>
                      <Textarea id="c-message" name="message" rows={5} value={form.message} onChange={handleChange} placeholder="Tell us about your requirement..." />
                    </div>
                    <Button type="submit" disabled={sending} className="bg-[#00ab96] hover:bg-[#0b8f7e] text-white rounded-full h-12 px-7">
                      <Send className="h-4 w-4 mr-2" /> {sending ? "Sending..." : "Send message"}
                    </Button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
